import type { Role } from './scope'
/** Canonical navigation — the handover ruling's menu (12/10/2026, answer 1; Version 21). One definition;
 *  the sidebar, routes and breadcrumbs all read from here. Never fork it. */
export interface NavItem { label: string; path: string; icon: string; children?: NavItem[]; /** other pages that belong to this item */ also?: string[]
  /** a section that is a page itself (« Clients »): its header opens it and lights up on it, its children are its other pages */
  page?: boolean }
export interface NavGroup { label: string | null; items: NavItem[] }

/* The menu in two groups (handover ruling 12/10, answer 1 — « follow the new package »; Version 21, the v3.3 shell):
   working: Accueil · Campagnes · Supports · Bibliothèques (Roues · Cadeaux · Demandes) · Résultats · Clients (Cadeaux gagnés)
   · Messages; the establishment's utilities: Équipe et caisses · Paramètres (its pages) · Aide. « Valider un cadeau » is the
   till door for every role, outside both groups, under the establishment (TILL_DOOR). A section opens its pages while the
   merchant is in it.
   Retired by name (the ruling): D-70's six words and « Suivre » as a menu word; « Demandes and Cadeaux leave the menu: they are
   tabs of Campagnes » (its empty CAMPAGNES_TABS list went with B's menu re-point, lib-pack-18); D-63's « Vos roues » is a tab of Cadeaux again; gate-go word 10's « Vos demandes folds
   into the builder » — a demande is a library page again (its other half stands: the platforms stay in Paramètres › Plateformes).
   Still retired (D-70, amending D-49 / D-63): « Configurer » and its numbering, « Identité visuelle » (→ Marque), « Aide et
   support » (→ Aide), « Établissement · Établissements · Ajouter un établissement » as three words, « Plus… ».
   The library pages keep their routes (Cadeaux.tsx serves /cadeaux and its ?onglet=; /roues and /demandes are its doors, App.tsx),
   and « Cadeaux gagnés » is /clients?vue=cadeaux (its door /cadeaux/gagnes); AppShell maps the page back to its item. */
export const NAV: NavGroup[] = [
  { label: null, items: [
    { label: 'Accueil', path: '/', icon: 'home' },
    { label: 'Campagnes', path: '/campagnes', icon: 'campaigns' },
    { label: 'Supports', path: '/supports', icon: 'displays' },
    { label: 'Bibliothèques', path: '/roues', icon: 'experiences', children: [
      { label: 'Roues', path: '/roues', icon: 'experiences' },
      { label: 'Cadeaux', path: '/cadeaux', icon: 'rewards' },
      { label: 'Demandes', path: '/demandes', icon: 'message' },
    ]},
    { label: 'Résultats', path: '/resultats', icon: 'analytics' },
    { label: 'Clients', path: '/clients', icon: 'customers', page: true, children: [
      { label: 'Cadeaux gagnés', path: '/cadeaux/gagnes', icon: 'rewards' },
    ]},
    { label: 'Messages', path: '/messages', icon: 'message' },
  ]},
  { label: 'Votre établissement', items: [
    { label: 'Équipe et caisses', path: '/equipe', icon: 'team' },
    { label: 'Paramètres', path: '/parametres', icon: 'settings', children: [
      { label: 'Mon établissement', path: '/etablissement', icon: 'building' },
      { label: 'Établissements', path: '/etablissements', icon: 'locations' },
      { label: 'Marque', path: '/identite', icon: 'style' },
      { label: 'Plateformes', path: '/parametres/plateformes', icon: 'destinations' },
      /* batch 21 · Builder B's batch-22 pages (Scope v5 §4): the four Paramètres pages by their real doors */
      { label: 'Intégrations', path: '/parametres/integrations', icon: 'plug' },
      { label: 'Organisation', path: '/parametres/organisation', icon: 'edit' },
      /* FQ-I4 (the record's Paramètres: … · Offre): the Offre page and its sections; the team's frames and BI-10 are
         Compte et accès's doors, so they light it */
      { label: 'Offre', path: '/parametres/offre', icon: 'billing',
        also: ['utilisation', 'factures', 'paiement', 'profil', 'essai', 'annuler', 'etablissement'].map(p => `/parametres/${p}`) },
      { label: 'Compte et accès', path: '/parametres/compte', icon: 'team', also: ['/parametres/equipe', '/parametres/espace'] },
    ]},
    { label: 'Aide', path: '/aide', icon: 'help' },
  ]},
]

/* The « Valider un cadeau » door (gift-wallet review B/C and P0.3, MA-3, errata B1, Version 19; Version 21 places it under the
   establishment, outside both groups): for every role. It opens the till on this device, registering it first when the person
   can do that in one tap (routes/equipe/Caisse.tsx). */
export const TILL_DOOR: NavItem = { label: 'Valider un cadeau', path: '/caisse', icon: 'check' }

/* lib-pack-17 (WF-117, gap-map ruling A: the test is guidance, a staff QR shows whenever its support serves a live principale):
   a Collaborateur's sidebar is « Mon QR · Valider un cadeau · Aide » — her own QR, the till, help; nothing to manage. The page
   is Builder B's (routes/equipe, pack equipe-qr): « Mon QR » is lit on /mon-qr only (lib-pack-18 dropped the Équipe stop-gap `also`).
   The world switcher offers a « Collaborateur » session (mocks/WorldSwitcher). */
export const MON_QR: NavItem = { label: 'Mon QR', path: '/mon-qr', icon: 'qr' }
export const STAFF_NAV: NavGroup[] = [
  { label: null, items: [MON_QR] },
  { label: 'Votre établissement', items: [{ label: 'Aide', path: '/aide', icon: 'help' }] },
]

/** Every leaf the sidebar can point at, sections and the till door included. */
export const NAV_ALL: NavItem[] = [...NAV.flatMap(g => g.items).flatMap(i => [i, ...(i.children ?? [])]), TILL_DOOR, MON_QR]

/* ---------- what the workspace decides ----------
   One establishment: « Mon établissement » (its page). Two or more: « Établissements » (the list and the context — D-31).
   Never both, never a list of one (D-63). */
export interface NavSignals { locations: number; /** the signed-in role; AppShell passes it, a caller that asks only about the establishments may omit it */ role?: Role }
export const NAV_DROP: Record<string, (s: NavSignals) => boolean> = {
  '/etablissement': s => s.locations >= 2,
  '/etablissements': s => s.locations < 2,
  /* Version 17 (billing states, ACCEPTED 10/10; handover ruling 12/10: billing is the owner's): « Offre » and its sections
     leave the menu for every other role. The doors to it elsewhere follow (Accueil's quota line, Messages' quota facts). */
  '/parametres/offre': s => s.role !== undefined && s.role !== 'owner',
}
/** The sidebar for these signals. `keep` is the current path — an item the merchant is on is never hidden under him. */
export function visibleNav(s: NavSignals, keep?: string): NavGroup[] {
  if (s.role === 'staff') return STAFF_NAV
  const show = (i: NavItem) => { const onIt = keep !== undefined && keep !== '/' && keep.startsWith(i.path); const drop = NAV_DROP[i.path]; return !(drop && drop(s) && !onIt) }
  return NAV.map(g => ({ label: g.label, items: g.items.filter(show).map(i => i.children ? { ...i, children: i.children.filter(show) } : i) })).filter(g => g.items.length > 0)
}

/** The sidebar item a path belongs to — a child first (the longest path wins: /cadeaux/gagnes is Clients', not Bibliothèques'),
    then its section, then a top item or the till door. `/` only matches itself. */
export function navItemFor(pathname: string, role?: Role): { item: NavItem; section?: NavItem } | undefined {
  const under = (path: string) => pathname === path || pathname.startsWith(path + '/')
  const hit = (i: NavItem) => i.path === '/' ? pathname === '/' : (under(i.path) || (i.also ?? []).some(under))
  /* a Collaborateur's menu is her own (STAFF_NAV): « Mon QR » lights on Équipe et caisses, where it opens for now */
  const tops = role === 'staff' ? [...STAFF_NAV.flatMap(g => g.items), TILL_DOOR] : [...NAV.flatMap(g => g.items), TILL_DOOR]
  const kids = tops.flatMap(top => (top.children ?? []).filter(hit).map(c => ({ item: c, section: top }))).sort((a, b) => b.item.path.length - a.item.path.length)
  if (kids[0]) return kids[0]
  /* sections and top items — the longest path wins (/parametres alone is the section) */
  const hits = tops.filter(hit).sort((a, b) => b.path.length - a.path.length)
  return hits[0] ? { item: hits[0] } : undefined
}
